import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";

export interface GGPick {
  tier: "club" | "international";
  fixture_key: string;
  fixture_label: string | null;
  league: string | null;
  kickoff: string | null;
  home_code: string | null;
  away_code: string | null;
  over_15_prob: number | null;
  over_15_odds: number | null;
  over_25_prob: number | null;
  over_25_odds: number | null;
  over_35_prob: number | null;
  over_35_odds: number | null;
  book_over_35: number | null;
  panel_status: "confirmed" | "neutral" | "opposed" | "no_data" | null;
  // The agent-believed goal NUMBER for this fixture (computed in the builder from
  // the swarm verdicts, model-sanity-checked). null = panel fades goals / no read.
  believed_line: "1.5" | "2.5" | "3.5" | null;
  believed_prob: number | null;     // model prob at the believed line
  believed_agree: number | null;    // # agents who SEE at least this many goals
  believed_disagree: number | null; // # agents leaning under
}

// Fixtures grouped by the goal NUMBER the agents believe will appear.
export interface GGTier {
  over35: GGPick[];   // "4+ goals" — believed Over 3.5
  over25: GGPick[];   // "3+ goals" — believed Over 2.5
  over15: GGPick[];   // "2+ goals" — believed Over 1.5
}
export interface GoalsGaloreVM {
  loading: boolean;
  error: string | null;
  club: GGTier;
  international: GGTier;
  refetch: () => Promise<void>;
}

const EMPTY: GGTier = { over35: [], over25: [], over15: [] };

// Goals Galore shows, per fixture, the goal NUMBER the agents believe will appear
// (Over 1.5 / 2.5 / 3.5 — even Over 1.5 is a goals read). The builder already did
// the honest work: believed_line is the highest line a cluster of agents foresees
// (model-sanity-checked), or null when the panel fades goals / there's no read.
// So the view is a pure grouping by believed_line — no fixture is shown on a line
// the agents don't actually back, and none is double-counted across lines.
function byBelief(rows: GGPick[]): GGTier {
  const live = rows.filter((r) => r.believed_line != null);
  const pick = (line: "1.5" | "2.5" | "3.5") =>
    live.filter((r) => r.believed_line === line)
      .sort((a, b) => (b.believed_prob ?? 0) - (a.believed_prob ?? 0));
  return { over35: pick("3.5"), over25: pick("2.5"), over15: pick("1.5") };
}

export function useGoalsGalore(): GoalsGaloreVM {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const supabase = useMemo(() => getBrowserClient() as any, []);
  const [s, setS] = useState<Omit<GoalsGaloreVM, "refetch">>({
    loading: true, error: null, club: EMPTY, international: EMPTY,
  });

  const fetchAll = useCallback(async () => {
    setS((p) => ({ ...p, loading: true, error: null }));
    try {
      const { data, error } = await supabase
        .from("goals_galore_picks").select("*").order("kickoff", { ascending: true });
      if (error) throw error;
      const rows = (data ?? []) as GGPick[];
      // Backstop de-dupe: a national-team fixture can leak into the club substrate
      // (priced via the_odds_api). International is the source of truth for those,
      // so drop any club row whose fixture matches an international one.
      const norm = (s: string | null) => (s ?? "").toLowerCase().replace(/\s+/g, " ").trim();
      const intlLabels = new Set(rows.filter((r) => r.tier === "international").map((r) => norm(r.fixture_label)));
      const clubRows = rows.filter((r) => r.tier === "club" && !intlLabels.has(norm(r.fixture_label)));
      setS({
        loading: false, error: null,
        club: byBelief(clubRows),
        international: byBelief(rows.filter((r) => r.tier === "international")),
      });
    } catch (e) {
      setS({ loading: false, error: e instanceof Error ? e.message : String(e), club: EMPTY, international: EMPTY });
    }
  }, [supabase]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);
  return { ...s, refetch: fetchAll };
}
